import type { IObservableArray } from 'mobx';
import { runInAction, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import type {
  Account,
  Billable,
  DisplayCreative,
  Event,
  Goal,
  Partner,
  Segment,
  Targetable,
  Targeting,
  TTDCampaign,
  User,
} from '@feathr/blackbox';
import { GEO_TARGETING_KINDS } from '@feathr/blackbox';
import {
  ActionBar,
  Button,
  ButtonValid,
  generateStepHash,
  parseStepFromHash,
  Spinner,
  useHashSync,
  usePoof,
  WizardV2,
} from '@feathr/components';
import AddCreativeButton from '@feathr/extender/components/creatives/AddCreativeButton';
import { validateStepCreatives } from '@feathr/extender/components/creatives/dimensionValidation.utils';
import {
  EditedGoalIdsContext,
  useEditedGoalIds,
} from '@feathr/extender/components/Goal/EditedGoalIdsContext';
import { getEffectiveCreativeDestinationUrls } from '@feathr/extender/components/Goal/Goal.predicates';
import {
  useBlackboxVersion,
  useCampaignText,
  useClearUnsavedChanges,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { flattenErrors, getIconForAction } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import createCampaignStore, { CampaignContext } from '../CampaignEditPage.context';
import { useChannelAvailability } from '../CampaignEditPage.useChannelAvailability';
import SaveCampaignButton, { save } from '../SaveCampaignButton';
import type { IEditedGoalIds } from '../StepGoals';
import StepGoals, { getGoalSegments, validateStepGoals } from '../StepGoals';
import { validateStepValue } from '../StepValue';
import WizardGeneralStep, {
  type IValidateStepGeneralConfig,
  validateStepGeneral,
} from '../WizardGeneralStep';
import { useCampaignValidation } from '.';
import type { IUniqueEmailsResult } from './AddWizard.useReconcileUniqueEmails';
import AdWizardAudienceStep, {
  getEphemeralTargetables,
  getTargetSegments,
  type IReachValidationResult,
  validateStepTargets,
} from './AdWizardAudienceStep';
import AdWizardBudgetStep, { validateStepBudget } from './AdWizardBudgetStep';
import AdWizardCreativesStep from './AdWizardCreativesStep';
import AdWizardFiltersStep, { validateStepFilters } from './AdWizardFiltersStep';
import AdWizardOptimizationStep, { validateStepOptimize } from './AdWizardOptimizationStep';
import AdWizardReviewStep, { validatePartner } from './AdWizardReviewStep';

import * as styles from './AdWizard.css';

interface IProps {
  campaign: TTDCampaign;
}

export interface ICampaignDetails {
  campaign: TTDCampaign;
  targetings: IObservableArray<Targeting>;
  targetingSegments?: Segment[];
  targetables?: Targetable[];
  goals: IObservableArray<Goal>;
  goalSegments?: Segment[];
  creatives: IObservableArray<DisplayCreative>;
  billable?: Billable;
  account: Account;
  event: Event;
  monetizationValue?: number;
  partner?: Partner;
  emailValidation: IUniqueEmailsResult;
  editedGoalIds?: IEditedGoalIds;
  /** Effective ad-creative destination URLs; see validateStepGoals. */
  creativeDestinationUrls?: string[];
  reachValidation: IReachValidationResult;
}

const getCompletedStep = (
  {
    account,
    campaign,
    creatives,
    goals,
    goalSegments,
    targetings,
    targetingSegments,
    targetables,
    emailValidation,
    editedGoalIds,
    reachValidation,
  }: ICampaignDetails,
  hideFilters: boolean,
  isOnProduction: boolean,
  user?: User,
  noMaxBudget?: boolean,
  generalConfig?: IValidateStepGeneralConfig,
): number => {
  // When hideFilters is true, step indices after Audience are shifted down by 1
  const offset = hideFilters ? 1 : 0;

  if (validateStepGeneral(campaign, generalConfig).length) {
    return 0;
  }
  if (
    targetings.length === 0 ||
    flattenErrors(
      validateStepTargets({
        campaign,
        targetings,
        segments: targetingSegments ?? [],
        targetables: targetables ?? [],
        emailValidation,
        reachValidation,
        isReachMinimumExempt: account.isInternal && !isOnProduction,
        // #8663: Omni gates reach at publish, not progression; other types keep the Audience gate.
        enforceReachMinimum: !campaign.isOmniCampaign,
      }),
    ).length
  ) {
    return 1;
  }
  // For OmniReach campaigns, don't advance past audience step if only geo targetings exist
  if (campaign.isOmniReachCampaign) {
    const hasOnlyGeoTargetings = targetings
      .filter((t) => !t.get('is_archived'))
      .every((t) => GEO_TARGETING_KINDS.includes(t.get('kind')));
    if (hasOnlyGeoTargetings) {
      return 1;
    }
  }
  if (!hideFilters && validateStepFilters(campaign, targetings).length) {
    return 2;
  }
  if (validateStepCreatives(campaign, creatives).length) {
    return 3 - offset;
  }
  if (flattenErrors(validateStepOptimize(campaign)).length) {
    return 4 - offset;
  }
  if (
    flattenErrors(validateStepBudget({ campaign, account, user, noMaxBudget, isOnProduction }))
      .length
  ) {
    return 5 - offset;
  }
  if (campaign.isMonetization && flattenErrors(validateStepValue(campaign)).length) {
    return 6 - offset;
  } else if (
    flattenErrors(
      validateStepGoals({
        goals,
        goalSegments,
        campaignObjective: campaign.get('objective'),
        creativeDestinationUrls: getEffectiveCreativeDestinationUrls(
          creatives,
          campaign.get('destination_url'),
        ),
        editedGoalIds,
        isDraftCampaign: campaign.isDraft,
      }),
    ).length
  ) {
    return 6 - offset;
  }
  return 7 - offset;
};

// TODO: incorporate new wizard hooks here #4560
const getCurrentStepErrors = (
  {
    account,
    campaign,
    creatives,
    goals,
    goalSegments,
    targetings,
    targetingSegments,
    targetables,
    partner,
    emailValidation,
    editedGoalIds,
    reachValidation,
  }: ICampaignDetails,
  hideFilters: boolean,
  isOnProduction: boolean,
  user?: User,
  noMaxBudget?: boolean,
  currentStep?: number,
  generalConfig?: IValidateStepGeneralConfig,
): string[] => {
  // #8663: only the navigation surfaces (Next, step dots) request a specific step; the aggregate
  // path (no step) is the publish gate. Omni enforces reach at publish, not during navigation.
  const enforceReachMinimum = !(currentStep !== undefined && campaign.isOmniCampaign);
  const generalErrors = validateStepGeneral(campaign, generalConfig);
  const audienceErrors = flattenErrors(
    validateStepTargets({
      campaign,
      targetings,
      segments: targetingSegments ?? [],
      targetables: targetables ?? [],
      emailValidation,
      reachValidation,
      isReachMinimumExempt: account.isInternal && !isOnProduction,
      enforceReachMinimum,
    }),
  );
  const filtersErrors = validateStepFilters(campaign, targetings);
  const creativesErrors = validateStepCreatives(campaign, creatives);
  const optimizationErrors = flattenErrors(validateStepOptimize(campaign));
  const budgetErrors = flattenErrors(
    validateStepBudget({ campaign, account, user, noMaxBudget, isOnProduction }),
  );
  const goalsErrors = campaign.isMonetization
    ? flattenErrors(validateStepValue(campaign))
    : flattenErrors(
        validateStepGoals({
          goals,
          goalSegments,
          campaignObjective: campaign.get('objective'),
          creativeDestinationUrls: getEffectiveCreativeDestinationUrls(
            creatives,
            campaign.get('destination_url'),
          ),
          editedGoalIds,
          isDraftCampaign: campaign.isDraft,
        }),
      );
  const reviewErrors = partner ? validatePartner(partner) : [];

  // Build step-to-errors map based on whether filters step is shown
  const stepErrors: string[][] = hideFilters
    ? [
        generalErrors,
        audienceErrors,
        creativesErrors,
        optimizationErrors,
        budgetErrors,
        goalsErrors,
        reviewErrors,
      ]
    : [
        generalErrors,
        audienceErrors,
        filtersErrors,
        creativesErrors,
        optimizationErrors,
        budgetErrors,
        goalsErrors,
        reviewErrors,
      ];

  if (typeof currentStep === 'number') {
    return stepErrors[currentStep] ?? [];
  }

  return stepErrors.flat();
};

function AdWizard({ campaign }: IProps): JSX.Element {
  const { Creatives, Billables, Events, Goals, Segments, Targetings, Targetables, Partners } =
    useStore();
  const account = useAccount();
  const location = useLocation();
  const { noMaxBudget } = useFlags();
  const user = useUser();
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { disableStopOrRepublishPastEndDate } = useCampaignText();
  // Per-session edited URL Visit goal IDs, shared by the validation gate (prop) and
  // URLVisitGoal (context).
  const editedGoalIds = useEditedGoalIds();
  const {
    childModels,
    grandchildModels,
    getValidationErrors,
    emailValidation,
    reachValidation,
    audienceData,
    estimatedReach,
  } = useCampaignValidation({
    campaign,
    editedGoalIds,
  });
  useClearUnsavedChanges({
    model: campaign,
    onCleanup: () => {
      Goals.clearApiCache();
    },
  });
  const { isOnProduction } = useBlackboxVersion();
  const poof = usePoof();

  const {
    isDraft,
    isGeofencingCampaign,
    isOmniCampaign,
    isOmniReachCampaign,
    isPublished,
    isStopped,
    isPublishing,
    isPastEndDate,
  } = campaign;
  // Published, publishing, or stopped: campaign has left draft, so save validates content.
  const isLiveCampaignState = isPublished || isPublishing || isStopped;
  const hideFilters = isGeofencingCampaign || isOmniReachCampaign;
  const creativesStepIndex = hideFilters ? 2 : 3;
  const generalConfig: IValidateStepGeneralConfig = {
    isOmniCampaign,
  };

  const defaultStep = parseStepFromHash(location.hash);

  const [currentStep, setCurrentStep] = useState(defaultStep ?? 0);

  // Update both state and URL hash when step changes
  const updateCurrentStep = (step: number): void => {
    setCurrentStep(step);
    // Use window.location.hash directly to avoid triggering navigation prompts
    window.location.hash = generateStepHash(step);
  };
  const [completedStep, setCompletedStep] = useState<number | undefined>(defaultStep);
  const [didCompleteStep, setDidCompleteStep] = useState<boolean>(false);
  // Latches once the wizard has loaded; see isLoading.
  const hasInitiallyLoadedRef = useRef(false);
  const [isPublishInProgress, setIsPublishInProgress] = useState(false);

  // Sync wizard step with manual URL hash changes (e.g., user edits #step2 to #step1).
  // Gate until the step resolves so we don't write the placeholder step 0 (#step1) mid-load.
  useHashSync({
    enabled: completedStep !== undefined,
    prefix: 'step',
    current: currentStep,
    completed: completedStep ?? 0,
    linear: true,
    onChange: setCurrentStep,
  });

  const partnerId = campaign.isMonetization ? campaign.get('parent') : undefined;
  const partner = partnerId ? Partners.get(partnerId) : undefined;

  const event = Events.get(campaign.get('event'));
  const billing = event.get('billing');
  const billable =
    !event.isPending && billing && billing.billable_id
      ? Billables.get(billing.billable_id)
      : undefined;

  const targetings = Targetings.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    pagination: { page_size: 1000 },
  });
  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });
  // Campaigns only have DisplayCreatives.
  const creatives = Creatives.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    ordering: ['-date_created'],
  }) as ListResponse<DisplayCreative>;

  const { availableChannels, hasDisplayCreatives, hasMetaCreatives } = useChannelAvailability({
    campaign,
    creatives: creatives.models,
  });

  const doCompleteStep = (): void => {
    if (
      !event.isPending &&
      !targetings.isPending &&
      !goals.isPending &&
      !creatives.isPending &&
      !emailValidation.isLoading &&
      !reachValidation.isLoading &&
      !didCompleteStep
    ) {
      const goalSegments = getGoalSegments(goals.models, Segments);
      const targetingSegments = getTargetSegments(targetings.models, Segments);
      const targetables = getEphemeralTargetables(targetings.models, Targetables, campaign);
      Promise.all([
        ...goalSegments.map((s) => when(() => !s.isPending)),
        ...targetingSegments.map((s) => when(() => !s.isPending)),
        ...targetables.map((t) => when(() => !t.isPending)),
      ]).then(() => {
        runInAction(() => {
          const step = getCompletedStep(
            {
              campaign,
              targetings: targetings.models,
              targetingSegments,
              targetables,
              goals: goals.models,
              goalSegments,
              creatives: creatives.models,
              billable,
              account,
              event,
              partner,
              emailValidation,
              editedGoalIds,
              reachValidation,
            },
            hideFilters,
            isOnProduction,
            user,
            noMaxBudget,
            generalConfig,
          );
          updateCurrentStep(defaultStep ?? step);
          setCompletedStep(step);
          setDidCompleteStep(true);
        });
      });
    }
  };

  useEffect(doCompleteStep, [
    account,
    billable,
    billable?.isPending,
    campaign,
    creatives.isPending,
    creatives.models,
    defaultStep,
    didCompleteStep,
    emailValidation.isLoading,
    reachValidation.isLoading,
    event,
    event.isPending,
    noMaxBudget,
    goals.isPending,
    goals.models,
    hideFilters,
    Segments,
    Targetables,
    targetings.isPending,
    targetings.models,
    user,
    partner,
  ]);

  function onNext(): void {
    const nextStep = currentStep + 1;
    updateCurrentStep(nextStep);
    if (nextStep > (completedStep ?? 0)) {
      setCompletedStep(nextStep);
    }
  }

  function onPrev(): void {
    updateCurrentStep(currentStep - 1);
  }

  const isWizardInitializing = currentStep === -1 || completedStep === undefined;
  const isDataPending =
    campaign.isPending ||
    goals.isPending ||
    event.isPending ||
    targetings.isPending ||
    creatives.isPending ||
    emailValidation.isLoading;
  // Spinner shows only until the first load; latching prevents a background refetch
  // (e.g. the event model) from blanking the loaded wizard, which read as a double load.
  if (!isWizardInitializing && !isDataPending) {
    hasInitiallyLoadedRef.current = true;
  }
  const isLoading =
    !hasInitiallyLoadedRef.current &&
    (isWizardInitializing || (!isPublishInProgress && isDataPending));

  // completedStep is defined whenever the latch is set; the guard also narrows it below.
  if (isLoading || completedStep === undefined) {
    return <Spinner className={styles.spinner} />;
  }

  const lastStep = hideFilters ? 6 : 7;

  function getErrors(step?: number): string[] {
    return getCurrentStepErrors(
      {
        campaign,
        targetings: targetings.models,
        targetingSegments: getTargetSegments(targetings.models, Segments),
        targetables: getEphemeralTargetables(targetings.models, Targetables, campaign),
        goals: goals.models,
        goalSegments: getGoalSegments(goals.models, Segments),
        creatives: creatives.models,
        billable,
        account,
        event,
        partner,
        emailValidation,
        editedGoalIds,
        reachValidation,
      },
      hideFilters,
      isOnProduction,
      user,
      noMaxBudget,
      step,
      generalConfig,
    );
  }

  const campaignStore = createCampaignStore({ targetings });
  // #8663: stepSpecificErrors (Next button) drops the reach block for Omni; allErrors keeps it for Publish.
  const stepSpecificErrors = getErrors(currentStep);
  const allErrors = getErrors();

  // Returns error status and errors for steps that have been visited and have validation errors
  const getStepValidation = (
    stepIndex: number,
  ): { status: 'error' | undefined; errors: string[] | undefined } => {
    // Only show error for steps the user has visited (at or before completed step)
    if (stepIndex > (completedStep ?? 0)) {
      return { status: undefined, errors: undefined };
    }
    const errors = getErrors(stepIndex);
    const hasErrors = errors.length > 0;
    return {
      status: hasErrors ? 'error' : undefined,
      errors: hasErrors ? errors : undefined,
    };
  };

  async function handlePublish(): Promise<void> {
    setIsPublishInProgress(true);
    await save({
      campaign,
      childModels,
      grandchildModels,
      shouldChangeState: true,
      t,
      accountId: account.id,
    });
    history.push(localUrl(campaign.getItemUrl()));
  }

  return (
    <EditedGoalIdsContext.Provider value={editedGoalIds}>
      <CampaignContext.Provider value={campaignStore}>
        <ActionBar
          left={
            <>
              <Button
                disabled={currentStep <= 0 || isLoading}
                onClick={onPrev}
                prefix={getIconForAction('previous')}
              >
                {t('Previous')}
              </Button>
              <ButtonValid
                disabled={currentStep === lastStep || isLoading || undefined}
                errors={stepSpecificErrors}
                name={'next_step'}
                onClick={onNext}
                suffix={getIconForAction('next')}
                type={isDraft ? 'primary' : 'secondary'}
              >
                {t('Next')}
              </ButtonValid>
            </>
          }
          right={
            <>
              <SaveCampaignButton
                campaign={campaign}
                childModels={childModels}
                grandchildModels={grandchildModels}
                key={'save'}
                shouldChangeState={false}
                showIcon={true}
                type={isLiveCampaignState ? 'primary' : 'secondary'}
                // We shouldn't allow saving invalid content to a published/stopped campaign
                validate={isLiveCampaignState ? getValidationErrors : undefined}
              />
              {(isDraft || isStopped || isPublishInProgress) && (
                <ButtonValid
                  disabled={
                    completedStep < 6 ||
                    isPublishing ||
                    isPastEndDate ||
                    isPublishInProgress ||
                    undefined
                  }
                  errors={allErrors}
                  name={'publish'}
                  onClick={handlePublish}
                  prefix={getIconForAction('publish')}
                  tooltip={isPastEndDate ? disableStopOrRepublishPastEndDate : undefined}
                  tooltipPosition={'top-end'}
                  type={'success'}
                >
                  {isPublishInProgress ? t('Publishing...') : t('Publish')}
                </ButtonValid>
              )}
            </>
          }
          usePortal={true}
        />
        <div data-appcues-campaign={campaign.get('_cls')}>
          <WizardV2
            actions={
              currentStep === creativesStepIndex ? (
                <AddCreativeButton
                  availableChannels={availableChannels}
                  campaign={campaign}
                  campaignId={campaign.id}
                  creatives={creatives.models}
                  eventId={campaign.get('event')}
                  onAppear={poof.animateAppear}
                  type={'secondary'}
                />
              ) : undefined
            }
            completed={completedStep}
            current={currentStep}
            isFullWidth={true}
            name={'ad-wizard'}
            onChange={updateCurrentStep}
          >
            <WizardV2.Step {...getStepValidation(0)} title={t('General')}>
              <WizardGeneralStep
                campaign={campaign}
                hasDisplayCreatives={hasDisplayCreatives}
                hasMetaCreatives={hasMetaCreatives}
                onNext={onNext}
              />
            </WizardV2.Step>
            <WizardV2.Step {...getStepValidation(1)} title={t('Audience')}>
              <AdWizardAudienceStep
                audienceData={audienceData}
                campaign={campaign}
                emailValidation={emailValidation}
                estimatedReach={estimatedReach}
                targetings={targetings}
              />
            </WizardV2.Step>
            {!hideFilters && (
              <WizardV2.Step {...getStepValidation(2)} title={t('Filters (optional)')}>
                <AdWizardFiltersStep campaign={campaign} targetings={targetings} />
              </WizardV2.Step>
            )}
            <WizardV2.Step {...getStepValidation(hideFilters ? 2 : 3)} title={t('Creatives')}>
              <AdWizardCreativesStep campaign={campaign} creatives={creatives.models} poof={poof} />
            </WizardV2.Step>
            <WizardV2.Step {...getStepValidation(hideFilters ? 3 : 4)} title={t('Optimization')}>
              <AdWizardOptimizationStep
                campaign={campaign}
                onNavigateToTargetsStep={(): void => updateCurrentStep(1)}
                targetings={targetings.models}
              />
            </WizardV2.Step>
            <WizardV2.Step {...getStepValidation(hideFilters ? 4 : 5)} title={t('Budget')}>
              <AdWizardBudgetStep account={account} campaign={campaign} targetings={targetings} />
            </WizardV2.Step>
            <WizardV2.Step {...getStepValidation(hideFilters ? 5 : 6)} title={t('Goals')}>
              <StepGoals campaign={campaign} goals={goals.models} />
            </WizardV2.Step>
            <WizardV2.Step {...getStepValidation(hideFilters ? 6 : 7)} title={t('Review')}>
              <AdWizardReviewStep
                campaign={campaign}
                creatives={creatives.models}
                creativesStepHref={`#${generateStepHash(creativesStepIndex)}`}
                getValidationErrors={getValidationErrors}
                goals={goals.models}
                targetings={targetings}
              />
            </WizardV2.Step>
          </WizardV2>
        </div>
      </CampaignContext.Provider>
    </EditedGoalIdsContext.Provider>
  );
}

export default observer(AdWizard);
